import { Bars3Icon } from "@heroicons/react/24/outline";
import React from "react";

/**
 * Mobile-only top app bar with a hamburger to open the nav drawer.
 *
 * Sticky-positioned (`sticky top-0`) so the hamburger stays reachable
 * while the user scrolls long content (Boxes/Outages/Uptime tables).
 * Hidden at `lg:` and up where the persistent left sidebar replaces it.
 *
 * Visual: matches the existing primary-blue sidebar styling so the
 * mobile UI feels like a slim version of the desktop one rather than
 * a different chrome.
 */
interface AppBarProps {
  onOpenDrawer: () => void;
  /** Optional per-route title (e.g., "Map", "Outages"). When omitted, the
   *  bar shows the Rebound Dynamics logo. */
  title?: string;
  /** Optional right-aligned action slot (e.g., a refresh button on the
   *  Map page). Rendered with `lg:hidden` like the rest of the bar. */
  rightSlot?: React.ReactNode;
}

export const AppBar: React.FC<AppBarProps> = ({
  onOpenDrawer,
  title,
  rightSlot,
}) => (
  <header className="sticky top-0 z-[1080] bg-primary-500 text-white shadow flex items-center justify-between px-3 py-2 lg:hidden">
    <button
      type="button"
      onClick={onOpenDrawer}
      aria-label="Open navigation menu"
      className="p-2 -ml-1 min-h-[44px] min-w-[44px] rounded-md hover:bg-primary-600 active:bg-primary-700 flex items-center justify-center"
    >
      <Bars3Icon className="h-6 w-6" />
    </button>
    {title ? (
      <div className="font-bold tracking-tight text-base">{title}</div>
    ) : (
      <div className="bg-white rounded-md px-2 py-1">
        <img
          src={`${process.env.PUBLIC_URL || ""}/Rebound_Logo.png`}
          alt="Rebound Dynamics"
          className="h-6 w-auto"
        />
      </div>
    )}
    <div className="min-w-[44px] flex items-center justify-end">
      {rightSlot}
    </div>
  </header>
);

export default AppBar;
